/**
 * ComponentPropsProcessor - Validates and cleans component props
 * Handles field name mapping, query validation, and external tool validation
 */

import { ensureQueryLimit, validateAndFixSqlQuery } from '../utils';
import { logger } from '../../utils/logger';
import { MAX_AGENT_QUERY_LIMIT } from '../constants';

/**
 * Executed tool info for validation
 */
export interface ExecutedToolInfo {
	id: string;
	name: string;
	outputSchema?: {
		fields?: Array<{
			name: string;
			type: string;
		}>;
	};
}

/**
 * Configuration for props processing
 */
export interface PropsProcessorConfig {
	providerName: string;
	defaultLimit: number;
	maxLimit?: number; // Maximum allowed row limit (defaults to MAX_AGENT_QUERY_LIMIT)
}

/**
 * Config keys that expect numeric fields
 */
const NUMERIC_CONFIG_KEYS = ['yAxisKey', 'valueKey', 'aggregationField', 'sizeKey'];

/**
 * Config keys that expect string fields
 */
const STRING_CONFIG_KEYS = ['xAxisKey', 'nameKey', 'labelKey', 'groupBy'];

/**
 * All config fields that need validation
 */
const CONFIG_FIELDS_TO_VALIDATE = [
	'xAxisKey', 'yAxisKey', 'valueKey', 'nameKey', 'labelKey',
	'groupBy', 'aggregationField', 'seriesKey', 'sizeKey',
	'xAggregationField', 'yAggregationField'
];

/**
 * Find the best matching field name from valid fields
 */
function findMatchingField(
	fieldName: string,
	configKey: string,
	validFieldNames: string[],
	fieldTypes: Record<string, string>,
	providerName: string
): string | null {
	if (!fieldName) return null;
	const lowerField = fieldName.toLowerCase();
	const validFieldNamesLower = validFieldNames.map(n => n.toLowerCase());

	// 1. Exact match (case-insensitive)
	const exactIdx = validFieldNamesLower.indexOf(lowerField);
	if (exactIdx !== -1) return validFieldNames[exactIdx];

	// 2. Partial match (contains)
	const containsMatches = validFieldNames.filter((_, i) =>
		validFieldNamesLower[i].includes(lowerField) || lowerField.includes(validFieldNamesLower[i])
	);
	if (containsMatches.length === 1) return containsMatches[0];

	// 3. Based on configKey type, pick the best field by data type
	if (NUMERIC_CONFIG_KEYS.includes(configKey)) {
		const numericFields = validFieldNames.filter(f => fieldTypes[f] === 'number');
		const match = numericFields.find(f =>
			f.toLowerCase().includes(lowerField) || lowerField.includes(f.toLowerCase())
		);
		if (match) return match;
		if (numericFields.length > 0) {
			logger.warn(`[${providerName}] No match for "${fieldName}", using first numeric field: ${numericFields[0]}`);
			return numericFields[0];
		}
	}

	if (STRING_CONFIG_KEYS.includes(configKey)) {
		const stringFields = validFieldNames.filter(f => fieldTypes[f] === 'string');
		const match = stringFields.find(f =>
			f.toLowerCase().includes(lowerField) || lowerField.includes(f.toLowerCase())
		);
		if (match) return match;
		if (stringFields.length > 0) {
			logger.warn(`[${providerName}] No match for "${fieldName}", using first string field: ${stringFields[0]}`);
			return stringFields[0];
		}
	}

	// Last resort: return the first field
	logger.warn(`[${providerName}] No match for "${fieldName}", using first field: ${validFieldNames[0]}`);
	return validFieldNames[0];
}

/**
 * Validate and correct field names in config against tool's outputSchema
 */
export function validateConfigFieldNames(
	config: any,
	outputSchema: { fields?: Array<{ name: string; type: string }> },
	providerName: string
): any {
	if (!outputSchema?.fields || !config) return config;

	const validFieldNames = outputSchema.fields.map(f => f.name);
	const fieldTypes = outputSchema.fields.reduce((acc: Record<string, string>, f) => {
		acc[f.name] = f.type;
		return acc;
	}, {});

	const correctedConfig = { ...config };

	// Validate standard config fields
	for (const configKey of CONFIG_FIELDS_TO_VALIDATE) {
		const fieldValue = correctedConfig[configKey];
		if (fieldValue && typeof fieldValue === 'string') {
			if (!validFieldNames.includes(fieldValue)) {
				const correctedField = findMatchingField(fieldValue, configKey, validFieldNames, fieldTypes, providerName);
				if (correctedField) {
					logger.warn(`[${providerName}] Correcting config.${configKey}: "${fieldValue}" → "${correctedField}"`);
					correctedConfig[configKey] = correctedField;
				}
			}
		}
	}

	// Also check series array if present (for multi-series charts)
	if (Array.isArray(correctedConfig.series)) {
		correctedConfig.series = correctedConfig.series.map((s: any) => {
			if (s.dataKey && typeof s.dataKey === 'string' && !validFieldNames.includes(s.dataKey)) {
				const correctedField = findMatchingField(s.dataKey, 'yAxisKey', validFieldNames, fieldTypes, providerName);
				if (correctedField) {
					logger.warn(`[${providerName}] Correcting series.dataKey: "${s.dataKey}" → "${correctedField}"`);
					return { ...s, dataKey: correctedField };
				}
			}
			return s;
		});
	}

	return correctedConfig;
}

/**
 * Validate externalTool reference against executed tools
 */
export function validateExternalTool(
	externalTool: { toolId: string } | null | undefined,
	executedTools: ExecutedToolInfo[] | undefined,
	providerName: string
): { valid: boolean; executedTool?: ExecutedToolInfo } {
	if (!externalTool) {
		return { valid: true };
	}

	const toolId = externalTool.toolId;
	const validToolIds = (executedTools || []).map(t => t.id);
	const isValidTool = toolId && typeof toolId === 'string' && validToolIds.includes(toolId);

	if (!isValidTool) {
		logger.warn(`[${providerName}] externalTool.toolId "${toolId}" not found in executed tools [${validToolIds.join(', ')}], setting to null`);
		return { valid: false };
	}

	const executedTool = executedTools?.find(t => t.id === toolId);
	return { valid: true, executedTool };
}

/**
 * Validate and fix query - removes invalid patterns and fixes SQL errors
 */
export function validateAndCleanQuery(
	query: string | { sql: string; values?: any; params?: any } | null | undefined,
	config: PropsProcessorConfig
): { query: any; wasModified: boolean } {
	if (!query) {
		return { query: null, wasModified: false };
	}

	let wasModified = false;
	let cleanedQuery = query;
	const queryStr = typeof query === 'string' ? query : query?.sql || '';

	// Check for invalid JSON parsing patterns
	if (queryStr.includes('OPENJSON') || queryStr.includes('JSON_VALUE')) {
		logger.warn(`[${config.providerName}] Query contains OPENJSON/JSON_VALUE (invalid - cannot parse tool result), setting query to null`);
		return { query: null, wasModified: true };
	}

	// Validate and fix common SQL syntax errors
	const { query: fixedQuery, fixed, fixes } = validateAndFixSqlQuery(queryStr);
	if (fixed) {
		logger.warn(`[${config.providerName}] SQL fixes applied to component query: ${fixes.join('; ')}`);
		wasModified = true;
		if (typeof cleanedQuery === 'string') {
			cleanedQuery = fixedQuery;
		} else if ((cleanedQuery as any)?.sql) {
			cleanedQuery = { ...(cleanedQuery as any), sql: fixedQuery };
		}
	}

	// Ensure query has proper LIMIT clause
	const maxLimit = config.maxLimit ?? MAX_AGENT_QUERY_LIMIT;
	if (typeof cleanedQuery === 'string') {
		const limitedQuery = ensureQueryLimit(cleanedQuery, config.defaultLimit, maxLimit);
		if (limitedQuery !== cleanedQuery) wasModified = true;
		cleanedQuery = limitedQuery;
	} else if ((cleanedQuery as any)?.sql) {
		const limitedSql = ensureQueryLimit((cleanedQuery as any).sql, config.defaultLimit, maxLimit);
		if (limitedSql !== (cleanedQuery as any).sql) wasModified = true;
		cleanedQuery = { ...(cleanedQuery as any), sql: limitedSql };
	}

	return { query: cleanedQuery, wasModified };
}

/**
 * Process and clean component props
 * - Validates externalTool references
 * - Corrects field names in config
 * - Validates and fixes queries
 */
export function processComponentProps(
	props: any,
	executedTools: ExecutedToolInfo[] | undefined,
	config: PropsProcessorConfig
): any {
	let cleanedProps = { ...props };

	// Validate externalTool
	if (cleanedProps.externalTool) {
		const { valid } = validateExternalTool(
			cleanedProps.externalTool,
			executedTools,
			config.providerName
		);

		if (!valid) {
			cleanedProps.externalTool = null;
		}
		// Skip field name validation for all external tools
		// - Database tools: fields come from SQL query aliases
		// - Excel/CSV tools: fields come from actual column names
		// - API tools: fields come from API response
		// The LLM generates correct field names based on tool schema/output
	}

	// Validate and clean query
	if (cleanedProps.query) {
		const { query } = validateAndCleanQuery(cleanedProps.query, config);
		cleanedProps.query = query;
	}

	// Log if both query and externalTool exist
	if (cleanedProps.query && cleanedProps.externalTool) {
		logger.info(`[${config.providerName}] Both query and externalTool exist, keeping both - frontend will decide`);
	}

	return cleanedProps;
}
